/* global BigInt */
import React from 'react';
import { retailPieceDraftAmount, validRetailPieceSale } from './distributorRetailPricing';
import StatusBadge from '../../components/StatusBadge';
import shippingOptions from '../../data/shippingOptions.json';
import { carrierLabel } from '../../utils/trackingUrl';
import { submitterLabel } from '../../utils/submitterName';
import { moneyMinor } from '../../utils/money';
import { formatDeliveryDate } from '../../utils/orderDates';
import { useLocale } from '../../i18n/LanguageContext';

export function distributorHoldLabel(order) {
  if (order?.businessContext !== 'distributor' || order.status !== 'submitted') return null;
  const code = order.distributorHold?.reviewCode;
  if (code === 'cash') return 'Awaiting distributor funding';
  if (code === 'cash_quote') return 'Delivery quote needs review';
  if (code === 'bank') return 'Awaiting available product units';
  return ['intent', 'authorization', 'validation', 'catalog', 'shipping', 'fulfillment'].includes(
    code
  )
    ? 'Order requires review before release'
    : null;
}

export function distributorShippingAdjustment(order) {
  const value = order?.shippingAdjustment;
  const day = (date) =>
    typeof date === 'string' &&
    /^20\d{2}-\d{2}-\d{2}$/.test(date) &&
    Number.isFinite(new Date(`${date}T00:00:00Z`).getTime()) &&
    new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) === date;
  return order?.businessContext === 'distributor' &&
    value &&
    day(value.previousDeliveryDate) &&
    day(value.deliveryDate) &&
    value.deliveryDate > value.previousDeliveryDate &&
    value.deliveryDate === String(order.deliveryDate).slice(0, 10) &&
    value.shippingSpeed === order.shippingSpeed &&
    shippingOptions.includes(value.shippingSpeed) &&
    typeof value.adjustedAt === 'string' &&
    Number.isFinite(new Date(value.adjustedAt).getTime())
    ? value
    : null;
}

const validSale = (sale) =>
  validRetailPieceSale(sale) ||
  (sale?.schemaVersion === 1 &&
    sale.currency === 'USD' &&
    Number.isSafeInteger(sale.revision) &&
    sale.revision >= 0 &&
    moneyMinor(sale.productSubtotalMinor) !== null &&
    Array.isArray(sale.lines) &&
    sale.lines.length > 0 &&
    sale.lines.length <= 20 &&
    sale.lines.every(
      (line) =>
        moneyMinor(line?.amountMinor) !== null &&
        typeof line.size === 'string' &&
        Number.isSafeInteger(line.quantity) &&
        line.quantity > 0
    ) &&
    sale.lines.reduce((sum, line) => sum + BigInt(line.amountMinor), 0n) ===
      BigInt(sale.productSubtotalMinor));

// Preview follows the server's per-line USD half-up calculation, using verified product units.
export function distributorDraftAmount(lines, products, rate) {
  if (rate?.pricingBasis === 'piece') return retailPieceDraftAmount(lines, products, rate);
  if (rate && typeof rate === 'object') rate = rate.ratePerUnit;
  if (!Number.isFinite(rate) || rate < 0 || !/^\d+(?:\.\d{1,4})?$/.test(String(rate))) return null;
  const scaled = Math.round(rate * 10000);
  if (!Number.isSafeInteger(scaled) || !lines.length) return null;
  let total = 0n;
  for (const line of lines) {
    const quantity = Number(line.quantity);
    const units = products?.find((product) => product._id === line.product)?.unitsPerPiece;
    if (
      !Number.isSafeInteger(quantity) ||
      quantity < 1 ||
      quantity > 1000 ||
      !Number.isSafeInteger(units) ||
      units < 1
    )
      return null;
    total += (BigInt(quantity) * BigInt(units) * BigInt(scaled) + 50n) / 100n;
  }
  return total <= BigInt(Number.MAX_SAFE_INTEGER) ? moneyMinor(Number(total)) : null;
}

export function distributorSaleAmount(order, t) {
  return order?.businessContext === 'distributor' && validSale(order.distributorSaleSnapshot)
    ? moneyMinor(order.distributorSaleSnapshot.productSubtotalMinor)
    : t('Amount unavailable');
}

export function DistributorSaleDetails({ order, t, locale }) {
  const sale = order.distributorSaleSnapshot;
  const history = Array.isArray(order.distributorSaleHistory)
    ? order.distributorSaleHistory.filter(validSale)
    : [];
  return (
    <section className="mb-5 rounded-lg border border-[#e2e8f0] p-3" aria-label={t('Sale amount')}>
      <div className="flex items-center justify-between gap-3 text-xs">
        <h3 className="font-semibold text-[#0f172a]">{t('Product subtotal')}</h3>
        <strong>{distributorSaleAmount(order, t)}</strong>
      </div>
      <p className="mt-1 text-[10px] text-[#64748b]">
        {t(
          sale?.schemaVersion === 2
            ? 'Saved retail product prices per piece. Retail amounts never change distributor bank debits.'
            : 'Products only. This is not an invoice or a balance due.'
        )}
      </p>
      {validSale(sale) && (
        <ul className="mt-3 space-y-2 text-xs" aria-label={t('Product line amounts')}>
          {sale.lines.map((line, index) => (
            <li key={index} className="flex justify-between gap-3">
              <span>
                {order.lineItems?.[index]?.product?.name || t('Product')} · {line.size} ·{' '}
                {t('Qty:')} {line.quantity}
                {sale.schemaVersion === 2 && (
                  <>
                    {' '}
                    {' \u00b7 '}
                    {t('Retail: {price} per piece', { price: moneyMinor(line.pricePerPieceCents) })}
                  </>
                )}
              </span>
              <span className="shrink-0">{moneyMinor(line.amountMinor)}</span>
            </li>
          ))}
        </ul>
      )}
      {history.length > 0 && (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer font-medium">{t('Previous sale amounts')}</summary>
          <ol className="mt-2 space-y-2">
            {history.map((entry) => (
              <li key={entry.revision} className="flex flex-wrap justify-between gap-2">
                <span>
                  {t('Revision {revision}', { revision: entry.revision + 1 })} ·{' '}
                  {entry.acceptedAt && Number.isFinite(new Date(entry.acceptedAt).getTime())
                    ? new Date(entry.acceptedAt).toLocaleString(locale)
                    : t('Not set')}
                </span>
                <span>{moneyMinor(entry.productSubtotalMinor)}</span>
              </li>
            ))}
          </ol>
        </details>
      )}
    </section>
  );
}

// Read-only distributor fields use the existing order contract; no inferred events or defaults.
export function DistributorOrderMetadata({ order, t }) {
  const locale = useLocale();
  const adjustment = distributorShippingAdjustment(order);
  const holdLabel = distributorHoldLabel(order);
  return (
    <>
      {holdLabel && (
        <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900">{t(holdLabel)}</p>
      )}
      {adjustment && (
        <div role="status" className="rounded-lg bg-blue-50 p-3 text-xs text-slate-800">
          <strong>{t('Delivery date updated')}</strong>
          <p className="mt-1">
            {t(
              'Your requested delivery date moved from {previous} to {current} using the same shipping service. Delivery times are not guaranteed.',
              {
                previous: formatDeliveryDate(adjustment.previousDeliveryDate, locale),
                current: formatDeliveryDate(adjustment.deliveryDate, locale),
              }
            )}
          </p>
        </div>
      )}
      <div className="flex justify-between gap-4">
        <p className="text-[10px] font-medium text-[#64748b]">{t('Shipping option')}</p>
        <p className="text-xs font-medium text-[#0f172a] text-right">
          {shippingOptions.includes(order.shippingSpeed) ? t(order.shippingSpeed) : t('Not set')}
        </p>
      </div>
      <div className="flex justify-between gap-4">
        <p className="text-[10px] font-medium text-[#64748b]">{t('Preferred shipper')}</p>
        <p className="text-xs font-medium text-[#0f172a] text-right">
          {['ups', 'fedex'].includes(order.preferredCarrier)
            ? carrierLabel(order.preferredCarrier)
            : t('Not set')}
        </p>
      </div>
      <div className="flex justify-between gap-4">
        <p className="text-[10px] font-medium text-[#64748b]">{t('Submitted by')}</p>
        <p className="text-xs font-medium text-[#0f172a] text-right">
          {submitterLabel(order.patient || order.salesRep, t)}
        </p>
      </div>
    </>
  );
}

export function DistributorOrderHistory({ order, t, locale }) {
  return (
    order.statusHistory?.length > 0 && (
      <section className="mb-5" aria-label={t('Order history')}>
        <h3 className="text-[10px] font-semibold text-[#64748b] uppercase tracking-wider mb-2">
          {t('Order history')}
        </h3>
        <ol className="space-y-2 text-xs">
          {order.statusHistory.map((entry, index) => (
            <li key={index} className="flex flex-wrap items-center justify-between gap-2">
              <StatusBadge
                status={
                  ['submitted', 'approved', 'shipped', 'completed', 'cancelled'].includes(
                    entry.status
                  )
                    ? entry.status === 'submitted'
                      ? 'Order received \u2014 processing'
                      : entry.status
                    : 'Status unavailable'
                }
              />
              <span>
                {entry.changedAt && !Number.isNaN(new Date(entry.changedAt).getTime())
                  ? new Date(entry.changedAt).toLocaleString(locale)
                  : t('Not set')}
              </span>
            </li>
          ))}
        </ol>
      </section>
    )
  );
}
